import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  classifyEntries,
  osloIsoDate,
  type BreakdownAssignment,
  type BreakdownContext,
  type BreakdownEntry,
  type BreakdownProject,
  type ClassifiedEntry,
} from "@/lib/capacity-breakdown";

export interface CapacityBreakdownData {
  classified: ClassifiedEntry[];
  projects: Map<string, BreakdownProject & { project_code: string | null }>;
  /** userId → Oslo-datoer med minst én føring. */
  loggedDates: Map<string, Set<string>>;
  /** userId → Oslo-dato → førte timer. */
  hoursByDay: Map<string, Map<string, number>>;
  absences: Array<{ user_id: string; start_date: string; end_date: string; status: string | null; hours: number | null }>;
  holidays: Set<string>;
}

type AssignmentRow = {
  id: string;
  user_id: string;
  budget_line_id: string;
  task_status: string;
  label: string | null;
  budget_lines: { description: string | null; template_id: string | null } | null;
};

/**
 * Førte timer i året fordelt på kategoriene i teamets faktureringsgrad
 * (VIB-2990), pluss fravær og helligdager for «ikke ført». Kapasitet og grad
 * kommer fra useBillingRates, så tallene ikke kan skille lag.
 */
export function useCapacityBreakdown(year: number, enabled = true) {
  return useQuery({
    queryKey: ["capacity-breakdown", year],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CapacityBreakdownData> => {
      const from = `${year}-01-01`;
      const to = `${year + 1}-01-01`;

      const { data: reisetid } = await supabase
        .from("offer_line_templates")
        .select("id")
        .eq("is_medgatt", true)
        .eq("name", "Reisetid (medgått)")
        .maybeSingle();

      const [entries, projects, assignments, reisetidLines, absenceRes, holidayRes] = await Promise.all([
        fetchAllPaginated<BreakdownEntry>(
          "time_entries",
          "user_id, project_id, budget_line_id, assignment_id, start_date, duration_seconds",
          (q) => q.is("deleted_at", null).gte("start_date", from).lt("start_date", to),
        ),
        fetchAllPaginated<BreakdownProject & { project_code: string | null }>(
          "projects",
          "id, name, project_code, is_internal, project_type",
          (q) => q.is("deleted_at", null),
        ),
        fetchAllPaginated<AssignmentRow>(
          "budget_line_assignments",
          "id, user_id, budget_line_id, task_status, label, budget_lines(description, template_id)",
        ),
        reisetid?.id
          ? fetchAllPaginated<{ id: string }>("budget_lines", "id", (q) => q.eq("template_id", reisetid.id))
          : Promise.resolve([]),
        supabase.rpc("absence_capacity", { p_from: from, p_to: `${year}-12-31` }),
        supabase.from("company_holidays").select("date").gte("date", from).lt("date", to),
      ]);
      if (absenceRes.error) throw absenceRes.error;
      if (holidayRes.error) throw holidayRes.error;

      const byId = new Map<string, BreakdownAssignment>();
      const byUserLine = new Map<string, BreakdownAssignment>();
      for (const a of assignments) {
        const row: BreakdownAssignment = {
          id: a.id,
          user_id: a.user_id,
          budget_line_id: a.budget_line_id,
          task_status: a.task_status,
          label: a.label,
          lineDescription: a.budget_lines?.description ?? null,
          templateId: a.budget_lines?.template_id ?? null,
        };
        byId.set(a.id, row);
        byUserLine.set(`${a.user_id}:${a.budget_line_id}`, row);
      }

      const ctx: BreakdownContext = {
        projects: new Map(projects.map((p) => [p.id, p])),
        assignments: byId,
        assignmentByUserLine: byUserLine,
        reisetidTemplateId: reisetid?.id ?? null,
        lineTemplate: new Map(reisetidLines.map((l) => [l.id, reisetid?.id ?? null])),
      };

      const loggedDates = new Map<string, Set<string>>();
      const hoursByDay = new Map<string, Map<string, number>>();
      for (const e of entries) {
        if (!e.start_date) continue;
        const date = osloIsoDate(e.start_date);
        const set = loggedDates.get(e.user_id) ?? new Set<string>();
        set.add(date);
        loggedDates.set(e.user_id, set);
        const days = hoursByDay.get(e.user_id) ?? new Map<string, number>();
        days.set(date, (days.get(date) ?? 0) + Number(e.duration_seconds ?? 0) / 3600);
        hoursByDay.set(e.user_id, days);
      }

      return {
        classified: classifyEntries(entries, ctx, year),
        projects: new Map(projects.map((p) => [p.id, p])),
        loggedDates,
        hoursByDay,
        absences: absenceRes.data ?? [],
        holidays: new Set((holidayRes.data ?? []).map((h: { date: string }) => h.date)),
      };
    },
  });
}
